/**
 * The whole C3 wizard's state — a 1:1 port of Android's `OrderDraft.kt`.
 *
 * This is the single value persisted across a reload: docs/20 §3 requires that a draft survives
 * the app being killed on any step, and in a browser that means every tab close, every crash and
 * every service-worker update.
 *
 * **Everything display-related is denormalized onto the draft**, not looked up when rendering:
 * the car's line, the payment method's label, the city's limits, the delivery grid. The reason is
 * always the same one — a draft resumed straight at the summary step has none of the fetched
 * lists that produced those strings, and re-fetching to render a review screen is how a review
 * screen ends up showing a spinner or, worse, different numbers than the ones agreed to.
 *
 * `serviceAreaAvailable` is `null` until the first `/v1/consumer/service-area` answer for the
 * current pin arrives, and never defaults to `true`: an interrupted check must not silently
 * unblock the address step.
 */
import type { DeliveryWindow } from './deliveryWindow';
import type { ServiceHours } from './serviceHours';

/** Which quantity mode the fuel step is in. */
export type FuelMode = 'fullTank' | 'liters';

/** «Вы будете на месте?» */
export type Presence = 'withMe' | 'withoutMe';

/**
 * The time step's three choices.
 *
 * `urgent` is the paid one — it puts the order at the front of the dispatch queue and adds the
 * city's surcharge; `asap` is the plain «как освободится». The wire values are the backend's
 * `delivery_speed`.
 */
export type TimeMode = 'urgent' | 'asap' | 'scheduled';

export const TIME_MODE_WIRE: Record<TimeMode, string> = {
  urgent: 'URGENT',
  asap: 'NEAREST',
  scheduled: 'SCHEDULED',
};

/**
 * One more car on the same выезд — «заправить ещё одну по этому же адресу».
 *
 * `fuelTypeCode` and `tankVolumeLiters` are denormalized off the picked car because the volume
 * step validates this car's litres against *its* tank, and swapping the primary car has to know
 * which extras still burn the same fuel.
 *
 * `brand` is separate even though it is also the first word of `label`: the first word is not the
 * brand — «Alfa Romeo Giulia», «Land Rover Discovery» and «Great Wall Poer» all start with half
 * of one. The row needs it to show the emblem.
 */
export interface ExtraCar {
  carId: string;
  label: string;
  liters: string;
  fuelTypeCode: string | null;
  tankVolumeLiters: string | null;
  brand: string;
}

/** The 6 internal steps of C3 — one screen, six sheet contents. */
export type OrderStep = 'address' | 'fuel' | 'presence' | 'time' | 'payment' | 'summary';

export interface OrderDraft {
  carId: string | null;
  carLabel: string | null;
  carBrand: string;
  fuelTypeCode: string | null;
  tankVolumeLiters: string | null;

  /** Beyond the primary car. Empty for an ordinary order, which is what every order used to be. */
  extraCars: ExtraCar[];

  latitude: number | null;
  longitude: number | null;
  addressLabel: string | null;

  /** `null` = pending or never checked. Blocks exactly as `false` does. */
  serviceAreaAvailable: boolean | null;
  serviceAreaCityLabel: string | null;

  /** The resolved city's per-order volume limits, as decimal strings. `null` until first check. */
  minOrderLiters: string | null;
  maxOrderLiters: string | null;

  /** How many cars this city fits into one выезд. `null` from a backend predating the field. */
  maxCarsPerOrder: number | null;

  /** The city's own window grid, denormalized at zone-check time like the limits above. */
  deliveryWindows: DeliveryWindow[];

  /** Minutes the driver leaves before the window opens. A hint on the time step, nothing more. */
  dispatchLeadMinutes: number | null;

  /**
   * Часы работы пятна — когда принимаются «Срочно» и «Ближайшая» (см. `serviceHours.ts`).
   * Снимок проверки зоны, как и сетка окон. `null` — ещё не проверяли или бэкенд часов не знает.
   */
  serviceHours: ServiceHours | null;

  /**
   * The chosen window, verbatim as the server sent it. Its `from`/`to` go back out as
   * `scheduled_from`/`scheduled_to` unchanged — the client never recomputes those boundaries.
   */
  selectedWindow: DeliveryWindow | null;

  comment: string;
  fuelMode: FuelMode;
  liters: string;
  /**
   * Whether the owner will be there — and it is never «unanswered».
   *
   * It used to be nullable, defaulting to `null` while the step DISPLAYED «Я буду на месте» as the
   * chosen option (`draft.presence ?? 'withMe'`). Somebody who agreed with the default and pressed
   * «Далее» found a dead button and nothing explaining it: the screen showed one answer and the
   * draft held none. A default that is shown must be a default that is stored.
   *
   * «Буду на месте» is the right one to start on: it is the common case and the one that demands
   * nothing further, while «меня не будет» carries two hard gates behind it.
   */
  presence: Presence;
  notificationsGranted: boolean;
  timeMode: TimeMode;

  /** Which saved card the draft will use. No hold is placed on this step. */
  paymentMethodId: string | null;
  /** «Brand · •••• 1234», set together with the id — the summary reads it straight from here. */
  paymentMethodLabel: string | null;

  /**
   * Платить по СБП вместо карты.
   *
   * Взаимоисключающе с `paymentMethodId`: сервер принимает ровно одно из двух и отказывает, если
   * прислать оба или ни одного. Оба переключателя держит `payWithSbp`/`payWithCard` в
   * `paymentChoice.ts` — чтобы «выбрал карту» и «выбрал СБП» не оказались правдой одновременно.
   *
   * Признак живёт на черновике, а не в состоянии шага: черновик переживает закрытую вкладку, и
   * выбранный способ оплаты обязан пережить её вместе с ним.
   */
  paysWithSbp: boolean;

  step: OrderStep;

  /**
   * Client-side idempotency key for `POST /v1/consumer/orders`.
   *
   * Generated once when the draft is created and resent **verbatim** on every retry, including a
   * retry after the tab was closed mid-submit. Never regenerated on resume — that is precisely
   * what makes retrying safe instead of silently creating a second order and a second hold.
   */
  externalId: string;
}

/**
 * How many cars fit in one выезд when the city has not said.
 *
 * Resolving an absent limit to a permissive number would let somebody add a car the backend then
 * refuses, so the fallback is the conservative one.
 */
export const DEFAULT_MAX_CARS_PER_ORDER = 3;

export function maxCarsPerOrderOrDefault(draft: OrderDraft): number {
  return draft.maxCarsPerOrder ?? DEFAULT_MAX_CARS_PER_ORDER;
}

/** How many cars the выезд currently has, primary included. */
export function tripCarCount(draft: OrderDraft): number {
  return (draft.carId === null ? 0 : 1) + draft.extraCars.length;
}

export function canAddAnotherCar(draft: OrderDraft): boolean {
  return tripCarCount(draft) < maxCarsPerOrderOrDefault(draft);
}

/**
 * A UUID for the idempotency key.
 *
 * `crypto.randomUUID` arrived in Safari 15.4, and this app deliberately still supports iOS 15 —
 * that is why the service worker is built as a classic worker rather than a module. On those
 * devices the fallback runs. It uses `getRandomValues`, which has been there since Safari 11, so
 * the fallback is still cryptographically random; only the formatting is hand-rolled.
 */
function randomUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  // Version 4, variant 10xx — the two fields RFC 4122 fixes.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Поля, от которых зависит сумма заказа. Правка любого из них — это уже ДРУГОЙ заказ, и ключ
 * идемпотентности у него должен быть свой.
 *
 * Зачем (аудит 13.09.2026). Ключ живёт весь черновик, а шлюз по нему сводит повтор к уже
 * сделанной авторизации. Человек прошёл 3-D Secure, вкладку банка закрыл, в приложение не
 * вернулся; через «Продолжить» поменял литры и оформил заново — с тем же ключом. Заказ вставал
 * на новую сумму, а банк держал старую. Сервер теперь сверяет сумму сам; здесь — вторая линия:
 * не приносить чужой по сумме ключ вовсе.
 *
 * Адрес, комментарий, присутствие и способ оплаты сюда не входят: они на сумму не влияют, а
 * смена карты после отказа банка обязана идти с ТЕМ ЖЕ ключом — так шлюз обходит спентый номер.
 */
const MONEY_KEYS: ReadonlySet<keyof OrderDraft> = new Set<keyof OrderDraft>([
  'carId',
  'extraCars',
  'fuelMode',
  'liters',
  'timeMode',
  'selectedWindow',
  'latitude',
  'longitude',
]);

/** Меняет ли правка сумму заказа — см. [`MONEY_KEYS`]. Одинаковые значения правкой не считаются. */
export function changesMoney(current: OrderDraft, patch: Partial<OrderDraft>): boolean {
  return (Object.keys(patch) as Array<keyof OrderDraft>).some(
    (key) => MONEY_KEYS.has(key) && !Object.is(current[key], patch[key]),
  );
}

/** Правка черновика — с новым ключом идемпотентности, если она меняет сумму. */
export function applyDraftPatch(current: OrderDraft, patch: Partial<OrderDraft>): OrderDraft {
  return changesMoney(current, patch)
    ? { ...current, ...patch, externalId: freshDraftKey() }
    : { ...current, ...patch };
}

/** Новый ключ идемпотентности — для правок, которые собирают черновик сами (`carEdits`). */
export function freshDraftKey(): string {
  return randomUuid();
}

/** A fresh draft. */
export function createOrderDraft(overrides: Partial<OrderDraft> = {}): OrderDraft {
  return {
    carId: null,
    carLabel: null,
    carBrand: '',
    fuelTypeCode: null,
    tankVolumeLiters: null,
    extraCars: [],
    latitude: null,
    longitude: null,
    addressLabel: null,
    serviceAreaAvailable: null,
    serviceAreaCityLabel: null,
    minOrderLiters: null,
    maxOrderLiters: null,
    maxCarsPerOrder: null,
    deliveryWindows: [],
    dispatchLeadMinutes: null,
    serviceHours: null,
    selectedWindow: null,
    comment: '',
    fuelMode: 'fullTank',
    liters: '',
    presence: 'withMe',
    notificationsGranted: false,
    timeMode: 'asap',
    paymentMethodId: null,
    paymentMethodLabel: null,
    paysWithSbp: false,
    step: 'address',
    externalId: randomUuid(),
    ...overrides,
  };
}
